// frontend/src/components/dashboard/FarmSummary.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useFarm } from '../../context/FarmContext';
import { useI18n } from '../../context/I18nContext';
import { calculateTotalSiteArea } from '../../utils/siteSizeUtils';
import { getSiteSizeLabel, STATUS_BADGES } from '../../constants/plotConstants';
import { useDeviceProvisioning } from '../../hooks/useDeviceProvisioning';
import PlotCard from './PlotCard';
import FarmMap from './FarmMap';
import './FarmSummary.css';

const FarmSummary = ({
  summary: propSummary,
  selectedPlot: propSelectedPlot,
  onSelectPlot: propOnSelectPlot,
  onAddSite,
  onDeviceProvisioned
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const { openAddDeviceModal, DeviceProvisioningModal } = useDeviceProvisioning(onDeviceProvisioned);

  const {
    farmSummary: contextSummary,
    selectedPlotId: contextSelectedPlotId,
    setSelectedPlotId: contextSetSelectedPlotId,
    loading
  } = useFarm();

  const summary = propSummary || contextSummary;
  const selectedPlot = propSelectedPlot !== undefined ? propSelectedPlot : contextSelectedPlotId;
  const onSelectPlot = propOnSelectPlot || contextSetSelectedPlotId;

  if (loading && !summary) {
    return <div className="farm-summary panel">Loading farm summary...</div>;
  }

  if (!summary) {
    return (
      <div className="farm-summary panel">
        <p>No farm summary available. Please select or add a farm.</p>
      </div>
    );
  }

  const {
    farmId,
    overallRisk = '--',
    plots = []
  } = summary;

  const totalArea = calculateTotalSiteArea(plots);
  const sizeRangeLabel = getSiteSizeLabel(totalArea);

  const handleAddSiteClick = () => {
    if (onAddSite) onAddSite();
    navigate('/farms/new');
  };

  const handleKeyDownAddSite = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleAddSiteClick();
    }
  };

  // Resolve risk styling using centralized status badges or risk theme
  const getRiskBadgeStyle = (risk) => {
    const normalizedRisk = String(risk).toLowerCase();
    if (STATUS_BADGES[normalizedRisk]) {
      return {
        backgroundColor: STATUS_BADGES[normalizedRisk].bg,
        color: STATUS_BADGES[normalizedRisk].text
      };
    }
    return {};
  };

  return (
    <div className="farm-summary panel">
      <div className="farm-summary-header">
        <h3>Farm Overview</h3>
      </div>

      <div className="farm-summary-layout">
        {/* Left Column (2/3 Main) */}
        <div className="farm-summary-main">
          {/* Summary Metric Cards */}
          <div className="summary-cards-grid">
            <div className="summary-card">
              <span className="card-label">{t('farmSummary.totalPlots') || 'Total Plots'}</span>
              <span className="card-value">{plots.length}</span>
            </div>
            <div className="summary-card">
              <span className="card-label">{t('farmSummary.totalArea') || 'Total Area'}</span>
              <span className="card-value">{sizeRangeLabel}</span>
            </div>
            <div className="summary-card">
              <span className="card-label">{t('farmSummary.totalRisk') || 'Overall Farm Risk'}</span>
              <span
                className={`card-value risk-badge risk-${String(overallRisk).toLowerCase()}`}
                style={getRiskBadgeStyle(overallRisk)}
              >
                {overallRisk}
              </span>
            </div>
          </div>

          {/* Plot Selector Grid - 2 Column 50/50 Layout */}
          <div className="plots-section">
            <h4>{t('farmSummary.monitoredPlots') || 'Monitored Plots'}</h4>

            <div
              className="plots-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                alignItems: 'stretch'
              }}
            >
              {plots.map((plot) => {
                const plotId = plot.site_id || plot.id || plot.plotId || plot.external_id;
                return (
                  <PlotCard
                    key={plotId}
                    plot={plot}
                    farmId={farmId}
                    isSelected={selectedPlot === plotId}
                    onSelectPlot={onSelectPlot}
                    onAddDevice={openAddDeviceModal}
                  />
                );
              })}

              {/* Add Plot Card - Auto 50% Width */}
              <div
                className="add-plot-card"
                onClick={handleAddSiteClick}
                onKeyDown={handleKeyDownAddSite}
                role="button"
                tabIndex={0}
                aria-label="Add a new plot"
                style={{
                  width: '100%',
                  minHeight: '160px',
                  padding: '16px',
                  borderRadius: '10px',
                  border: '2px dashed #cbd5e1',
                  backgroundColor: '#f8fafc',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <div style={{ textAlign: 'center', color: '#475569' }}>
                  <span style={{ fontSize: '28px', fontWeight: '300', display: 'block', marginBottom: '4px' }}>+</span>
                  <strong style={{ fontSize: '14px' }}>Add New Plot</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (1/3 Map) */}
        <div className="farm-summary-map-column">
          <FarmMap
            plots={plots}
            selectedPlotId={selectedPlot}
            onSelectPlot={onSelectPlot}
          />
        </div>
      </div>

      <DeviceProvisioningModal />
    </div>
  );
};

export default FarmSummary;
